import { ApiEndpoint } from './endpoint.interface';
import { ApiNavigation, NavigationGroup } from './navigation.interface';
import { APISpec, APITag } from './api-spec.interface';

/**
 * Build navigation structure from endpoints
 * @param api - The OpenAPI specification
 * @param endpoints - Array of parsed endpoints
 * @returns Navigation structure for UI
 */
export function buildNavigation(api: APISpec, endpoints: ApiEndpoint[]): ApiNavigation {
  const groupsMap = new Map<string, NavigationGroup>();

  // Create groups based on endpoint tags
  endpoints.forEach(endpoint => {
    endpoint.tags.forEach(tag => {
      let group = groupsMap.get(tag);

      if (!group) {
        const tagInfo = api.tags?.find((t: APITag) => t.name === tag);
        group = {
          name: tag,
          description: tagInfo?.description || generateTagDescription(tag),
          endpoints: [],
          expanded: true,
        };
        groupsMap.set(tag, group);
      }

      group.endpoints.push({
        id: endpoint.id,
        label: createEndpointLabel(endpoint),
        method: endpoint.method,
        path: endpoint.path,
        route: `/api-docs/${endpoint.id}`,
      });
    });
  });

  // Sort groups
  const priorityTags = ['Convert', 'Merge', 'Resize', 'Crop', 'FlipRotate', 'Async'];
  const sortedGroups = Array.from(groupsMap.values()).sort((a, b) => {
    const aPriority = priorityTags.indexOf(a.name);
    const bPriority = priorityTags.indexOf(b.name);

    if (aPriority !== -1 && bPriority !== -1) {
      return aPriority - bPriority;
    }
    if (aPriority !== -1) return -1;
    if (bPriority !== -1) return 1;

    return a.name.localeCompare(b.name);
  });

  return {
    title: api.info?.title || 'API Documentation',
    version: api.info?.version || '1.0.0',
    groups: sortedGroups,
  };
}

/**
 * Generate tag description
 * @param tag - The tag name
 * @returns Generated description
 */
export function generateTagDescription(tag: string): string {
  const descriptions: Record<string, string> = {
    'Convert': 'Image conversion operations',
    'Merge': 'Image merging operations',
    'Resize': 'Image resizing operations',
    'Crop': 'Image cropping operations',
    'FlipRotate': 'Image flip and rotate operations',
    'Async': 'Asynchronous operation status endpoints',
    'Other': 'Miscellaneous API endpoints',
  };

  return descriptions[tag] || `${tag} related operations`;
}

/**
 * Create a display label for an endpoint
 * @param endpoint - The endpoint to label
 * @returns Display label
 */
export function createEndpointLabel(endpoint: ApiEndpoint): string {
  if (endpoint.summary) {
    return endpoint.summary;
  }

  if (endpoint.operationId) {
    return endpoint.operationId
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  const pathParts = endpoint.path.split('/').filter(Boolean);
  const lastPart = pathParts[pathParts.length - 1] || 'root';
  const cleanPart = lastPart.replace(/\{[^}]+\}/g, '').replace(/-/g, ' ');

  return `${endpoint.method} ${cleanPart}`.trim();
}
